import { useState, useEffect } from "preact/hooks";
import { authHeader } from "../auth.ts";
import type { TelegramDocument } from "../types/document.ts";
import { TelegramViewer } from "./telegram-viewer.tsx";

interface TelegramEntry {
  id: number;
  senderId: number;
  recipientId: number;
  content: string;
  readAt: string | null;
  createdAt: string;
}

interface User {
  id: number;
  username: string;
  displayName: string;
}

interface Props {
  users: User[];
}

export function Mailbox({ users }: Props) {
  const [telegrams, setTelegrams] = useState<TelegramEntry[]>([]);
  const [selected, setSelected] = useState<TelegramEntry | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchInbox = () => {
    setLoading(true);
    fetch("/api/telegrams/inbox", { headers: authHeader() })
      .then((r) => r.json())
      .then((data) => {
        setTelegrams(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchInbox();
  }, []);

  const markRead = async (t: TelegramEntry) => {
    setSelected(t);
    if (!t.readAt) {
      await fetch(`/api/telegrams/${t.id}/read`, {
        method: "POST",
        headers: authHeader(),
      });
      setTelegrams((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, readAt: new Date().toISOString() } : x)),
      );
    }
  };

  const senderName = (id: number) => users.find((u) => u.id === id)?.displayName ?? "Unknown";

  if (selected) {
    let doc: TelegramDocument;
    try {
      doc = JSON.parse(selected.content);
    } catch {
      return <div class="text-red-600">Failed to parse telegram content</div>;
    }

    return (
      <div class="flex flex-col items-center gap-4">
        <div class="flex items-center gap-4">
          <button
            class="px-3 py-1 rounded-lg text-sm"
            style={{ background: "#FFF8E7", border: "1px solid #D4B078", color: "#5D4E37" }}
            onClick={() => setSelected(null)}
          >
            Back to inbox
          </button>
          <span class="text-sm" style={{ color: "#5D4E37" }}>
            From <strong>{senderName(selected.senderId)}</strong> &mdash;{" "}
            {new Date(selected.createdAt).toLocaleString()}
          </span>
        </div>
        <TelegramViewer document={doc} />
      </div>
    );
  }

  if (loading) return <div style={{ color: "#8B7355" }}>Loading...</div>;

  if (telegrams.length === 0)
    return (
      <div class="text-sm" style={{ color: "#8B7355" }}>
        No telegrams yet.
      </div>
    );

  return (
    <div class="w-full max-w-lg">
      <div class="flex flex-col gap-2">
        {telegrams.map((t) => (
          <button
            key={t.id}
            class="text-left px-4 py-3 rounded-xl"
            style={
              t.readAt
                ? { border: "1px solid #D4B078", background: "#FFF8E7" }
                : { border: "1.5px solid #C48A3F", background: "#F5E6B8", fontWeight: 600 }
            }
            onClick={() => markRead(t)}
          >
            <div class="flex justify-between items-center">
              <span class="text-sm">
                From <strong>{senderName(t.senderId)}</strong>
              </span>
              <span class="text-xs" style={{ color: "#8B7355" }}>
                {new Date(t.createdAt).toLocaleString()}
              </span>
            </div>
            {!t.readAt && (
              <span class="text-xs mt-1 block" style={{ color: "#C48A3F" }}>
                New
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
